import { prefersReducedMotion } from '../anim/motion';
import { resolveConfig } from '../config/resolver';
import type { ClickReleaseConfig, ConfigOverride } from '../config/types';
import { DUMMY_ENTRIES } from '../content/dummy-data';
import { filterByTags } from '../content/filter';
import { RemoteContentSource, StaticContentSource, type ContentSource } from '../content/sources';
import type { ReleaseEntry } from '../content/types';
import { LocalStorageSeenStore, type SeenStore } from '../state/seen-store';
import { countUnread } from '../state/unread';
import { applyMotion, applyTheme } from '../theme/theme';
import { Banner } from '../surfaces/banner';
import { ChangelogPanel } from '../surfaces/panel';
import { Toast } from '../surfaces/toast';
import { attachIndicator, type AttachHandle } from '../triggers/attach';
import { Emitter, type CrEvent, type Handler } from './events';
import { ShadowHost } from './host';

export interface InitOptions {
  /** The app / project id issued by ClickRelease. Sent as `appId` and namespaces local seen-state. */
  appId: string;
  /**
   * Origin of the ClickRelease backend, e.g. `https://cr-backend.clickterm.net`. When set, release
   * notes are fetched from the public-client API. When omitted, the SDK runs on bundled dummy data
   * (useful for local development and the demo).
   */
  apiBase?: string;
  /**
   * How far back to fetch notes, in days (the list endpoint's `timeframeDays`). Defaults to 30 —
   * a "recent updates" window. The server's own default is only 7; raise this for a longer feed.
   */
  timeframeDays?: number;
  /**
   * Character budget for the summarized list. When > 0 (default 240), the panel shows plain-text
   * summaries and loads the full HTML body on "Show more". Set to 0 to fetch full bodies upfront.
   */
  summaryLength?: number;
  /**
   * Restrict the changelog to notes carrying at least one of these tags (OR match, case-insensitive
   * and whitespace-trimmed). Omit or pass an empty array for no filtering. Applied to every surface —
   * changelog, banner, toast and the unread count — so `tags` scopes the whole widget.
   *
   * Filtering is done **client-side**: the public-client API returns each note's tags but has no
   * server-side tag filter, so the SDK selects locally (and it therefore works the same on the live
   * backend and the bundled dummy content). See DESIGN.md.
   */
  tags?: string[];
  /**
   * Presentation & behaviour overrides, deep-merged over the bundled defaults — this is where the
   * embedding client customizes look & feel: surface (modal/sidebar), theme colors (accent, text,
   * dot, background, …), banner colors, badge/toast placement, theme mode, etc. See
   * `ClickReleaseConfig` / `ConfigOverride`.
   */
  config?: ConfigOverride;
}

const DEFAULT_TIMEFRAME_DAYS = 30;
const DEFAULT_SUMMARY_LENGTH = 240;

/** Reduce a BCP-47 locale (`en-US`) to the primary subtag (`en`) for the API's `language` param. */
function primaryLanguage(locale: string): string | undefined {
  const tag = locale?.split('-')[0]?.trim().toLowerCase();
  return tag || undefined;
}

export interface BannerApi {
  show(): void;
  hide(): void;
}

/**
 * The SDK instance returned by `init()`. Wires the config/content/seen layers to the surfaces and
 * exposes the public API. All presentation comes from config (bundled defaults ← the embedding
 * client's `config`); the host also supplies identity and the element selectors it alone knows.
 */
export class ClickReleaseInstance {
  #opts: InitOptions;
  #host: ShadowHost;
  #emitter = new Emitter();
  #seen: SeenStore;
  #config: ClickReleaseConfig;
  #content: ContentSource;
  #entries: ReleaseEntry[] = [];
  #staticPageUrl: string | undefined;
  #ready: Promise<void>;
  #panel: ChangelogPanel;
  #banner: Banner;
  #toast: Toast;
  #attachments: AttachHandle[] = [];
  #destroyed = false;

  constructor(opts: InitOptions) {
    this.#opts = opts;
    this.#seen = new LocalStorageSeenStore(opts.appId);
    this.#host = new ShadowHost();

    // Presentation comes from the embedding client's `config`, deep-merged over the bundled
    // defaults. Content starts as bundled dummy data and is replaced by the backend fetch when
    // `apiBase` is set. Config is client-supplied only — the backend no longer sends it. See DESIGN.md.
    this.#config = resolveConfig(opts.config);
    this.#content = new StaticContentSource(DUMMY_ENTRIES);
    this.#applyChrome();

    this.#panel = new ChangelogPanel(this.#host.root, () => this.#emitter.emit('close'));
    this.#banner = new Banner(this.#host.root, () => void this.open());
    this.#toast = new Toast(this.#host.root, () => void this.open());

    this.#ready = this.#bootstrap();
  }

  /** Number of entries newer than the last-seen marker. */
  get unreadCount(): number {
    return countUnread(this.#entries, this.#seen.getLastSeenAt());
  }

  /** Open the changelog (modal or sidebar per config). Marks all entries as seen. */
  async open(): Promise<void> {
    await this.#ready;
    if (this.#destroyed) return;
    this.#panel.open(this.#entries, this.#config, id => this.#content.get(id), { staticPageUrl: this.#staticPageUrl });
    this.#emitter.emit('open');
    this.markAllSeen();
  }

  /** Close the changelog if open. */
  close(): void {
    this.#panel.close();
  }

  /** Overlay an unread badge on `selector` and open the changelog on click. Returns a detach fn. */
  attach(selector: string): () => void {
    const handle = attachIndicator(this.#host.root, selector, this.#config, () => void this.open());
    if (!handle) return () => {};
    this.#attachments.push(handle);
    handle.indicator.setCount(this.unreadCount);
    void this.#ready.then(() => {
      if (!this.#destroyed) handle.indicator.setCount(this.unreadCount);
    });
    return () => {
      handle.detach();
      this.#attachments = this.#attachments.filter(item => item !== handle);
    };
  }

  /** Show a corner toast for the latest entry. */
  async notify(): Promise<void> {
    await this.#ready;
    if (this.#destroyed) return;
    this.#toast.show(this.#entries[0] ?? null, this.#config);
  }

  get banner(): BannerApi {
    return {
      show: () => {
        void this.#ready.then(() => {
          if (!this.#destroyed) this.#banner.show(this.#entries[0] ?? null, this.#config);
        });
      },
      hide: () => this.#banner.hide(),
    };
  }

  /** Mark all current entries as seen and clear the unread indicators. */
  markAllSeen(): void {
    const newest = this.#entries[0];
    if (newest) this.#seen.setLastSeenAt(newest.publishedAt);
    this.#refreshUnread();
  }

  /** Forget the seen marker so all entries are unread again (useful for testing the badge). */
  resetSeen(): void {
    this.#seen.clear();
    this.#refreshUnread();
  }

  on(event: CrEvent, fn: Handler): void {
    this.#emitter.on(event, fn);
  }

  off(event: CrEvent, fn: Handler): void {
    this.#emitter.off(event, fn);
  }

  /** Tear down all DOM, observers and listeners. */
  destroy(): void {
    this.#destroyed = true;
    for (const handle of this.#attachments) handle.detach();
    this.#attachments = [];
    this.#panel.destroy();
    this.#banner.destroy();
    this.#toast.destroy();
    this.#emitter.clear();
    this.#host.destroy();
  }

  async #bootstrap(): Promise<void> {
    if (this.#opts.apiBase) {
      this.#content = new RemoteContentSource({
        apiBase: this.#opts.apiBase,
        appId: this.#opts.appId,
        // Content language is the explicit `config.language`; fall back to the locale's primary subtag.
        language: this.#config.language?.trim() || primaryLanguage(this.#config.locale),
        timeframeDays: this.#opts.timeframeDays ?? DEFAULT_TIMEFRAME_DAYS,
        summaryLength: this.#opts.summaryLength ?? DEFAULT_SUMMARY_LENGTH,
      });
      try {
        this.#entries = await this.#content.list();
        this.#staticPageUrl = this.#content.staticPageUrl;
      } catch (err) {
        // A real deployment must never fall back to dummy marketing copy — show nothing instead.
        console.warn('[clickrelease] failed to load release notes:', err);
        this.#entries = [];
        this.#staticPageUrl = undefined;
      }
    } else {
      this.#entries = await this.#content.list(); // bundled dummy data (dev / no-backend)
      this.#staticPageUrl = undefined;
    }
    // Tag filtering is client-side (the public-client API has no tag filter), so it applies to both
    // the remote and dummy paths and scopes every surface that reads `#entries`.
    this.#entries = filterByTags(this.#entries, this.#opts.tags);
    this.#refreshUnread();
  }

  #applyChrome(): void {
    applyTheme(this.#host.hostEl, this.#config);
    applyMotion(this.#host.hostEl, prefersReducedMotion(this.#config.reducedMotion));
  }

  #refreshUnread(): void {
    const count = this.unreadCount;
    for (const handle of this.#attachments) handle.indicator.setCount(count);
    this.#emitter.emit('unreadchange', count);
  }
}
